import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import type { Organization, Project } from "@anyseal/contracts";
import { portalClient } from "./api";
import { useAuth } from "./auth";

type WorkspaceValue = {
  organizations: Organization[];
  projects: Project[];
  organization?: Organization;
  project?: Project;
  setOrganizationId: (id: string) => void;
  setProjectId: (id: string) => void;
  loading: boolean;
  error: Error | null;
};
const WorkspaceContext = createContext<WorkspaceValue | null>(null);

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const [organizationId, setOrganizationIdState] = useState(() => localStorage.getItem("anyseal.portal.organization") ?? "");
  const [projectId, setProjectIdState] = useState(() => localStorage.getItem("anyseal.portal.project") ?? "");
  const organizationsQuery = useQuery({
    queryKey: ["portal", "organizations"],
    queryFn: () => portalClient.get<Organization[]>("/api/portal/v1/organizations"),
    enabled: status === "authenticated",
  });
  const organizations = organizationsQuery.data ?? [];
  const organization = organizations.find((item) => item.id === organizationId) ?? organizations[0];
  const projectsQuery = useQuery({
    queryKey: ["portal", "projects", organization?.id],
    queryFn: () => portalClient.get<Project[]>(`/api/portal/v1/organizations/${organization!.id}/projects`),
    enabled: status === "authenticated" && Boolean(organization?.id),
  });
  const projects = projectsQuery.data ?? [];
  const project = projects.find((item) => item.id === projectId) ?? projects[0];

  useEffect(() => {
    if (organization && organization.id !== organizationId) {
      localStorage.setItem("anyseal.portal.organization", organization.id);
    }
  }, [organization, organizationId]);
  useEffect(() => {
    if (project && project.id !== projectId) {
      localStorage.setItem("anyseal.portal.project", project.id);
    }
  }, [project, projectId]);

  const setOrganizationId = (id: string) => {
    setOrganizationIdState(id);
    setProjectIdState("");
    localStorage.setItem("anyseal.portal.organization", id);
    localStorage.removeItem("anyseal.portal.project");
  };
  const setProjectId = (id: string) => {
    setProjectIdState(id);
    localStorage.setItem("anyseal.portal.project", id);
  };
  const error = (organizationsQuery.error ?? projectsQuery.error) as Error | null;

  // loading=true until we have a definitive answer:
  // - auth still initializing (queries haven't been enabled yet)
  // - organizations actively fetching
  // - have an org but projects are still fetching
  const loading =
    status === "initializing" ||
    status === "authenticated" && organizationsQuery.isPending ||
    (Boolean(organization?.id) && projectsQuery.isPending);

  const value: WorkspaceValue = {
    organizations, projects, organization, project, setOrganizationId, setProjectId,
    loading, error,
  };
  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

export function useWorkspace(): WorkspaceValue {
  const value = useContext(WorkspaceContext);
  if (!value) throw new Error("WorkspaceProvider is missing");
  return value;
}

export const projectPath = (organizationId: string, projectId: string): string => `/api/portal/v1/organizations/${organizationId}/projects/${projectId}`;
